/** Programmatic Verify report PDF (pdf-lib) — web + mobile. */
/** Bump when verifying prod/local got the latest template (shown in PDF footer). */
export const IMEI_REPORT_PDF_TEMPLATE_REV = '2026-10-16-grid3'
import fontkit from '@pdf-lib/fontkit'
import {
  PDFDocument,
  StandardFonts,
  rgb,
  type PDFFont,
  type PDFPage,
  type RGB,
} from 'pdf-lib'

import { loadImeiReportPdfFontBytes } from './imeiReportPdfFonts'
import { drawPdfSectionIcon, drawPdfTrustCheck, drawPdfVerdictIcon, verdictMarkColors } from './imeiReportPdfIcons'

export type ImeiReportPdfChipTone = 'pass' | 'warning' | 'fail' | 'unverified' | 'not_applicable'

export type ImeiReportPdfSection = {
  id?: string
  title: string
  chip: string
  chipTone: ImeiReportPdfChipTone
  detail?: string
  /** Paid follow-up hint (same copy as web `.vr-check-row__follow-hint`). */
  hint?: string
  priceLabel?: string
}

export type ImeiReportPdfLabels = {
  reportLabel: string
  serviceTitle: string
  deviceLabel: string
  imeiMasked: string
  checkedAt: string
  metaLine?: string
  statusLabel: string
  statusTone: 'clean' | 'attention' | 'risk' | 'unknown' | 'invalid' | 'balance'
  resultHeadline: string
  /** When `vc.imeiReport.result.noIssuesFound`, result mark matches web (check, not !). */
  resultHeadlineKey?: string
  coverageLine: string
  summary: string
  trustPills: string[]
  checksHeading: string
  checksLead: string
  checksCoverageSummary: string
  sections: ImeiReportPdfSection[]
  secureLine: string
  privacyLine: string
  providerLine?: string
  /** Optional hero PNG/JPEG bytes (hub art or legacy phone). */
  phoneImageBytes?: Uint8Array
  /** When true, layout matches web `.vr-hero--hub-art` (no hero verdict pill). */
  heroHubArt?: boolean
  /** @deprecated Vector icons in PDF; PNG embed optional for legacy callers. */
  sectionIconPngBytes?: Record<string, Uint8Array>
  /** Optional embedded Unicode TTF bytes (skips CDN fetch when set). */
  fontRegularBytes?: Uint8Array
  fontBoldBytes?: Uint8Array
}

/** Strip diacritics for WinAnsi StandardFonts fallback. */
export function pdfSafeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\x20-\x7E]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function displayText(value: string, unicode: boolean): string {
  if (!value) return ''
  if (unicode) return value.replace(/\s+/g, ' ').trim()
  return pdfSafeText(value)
}

const NAVY = rgb(0.06, 0.17, 0.36)
const TEAL = rgb(0.08, 0.61, 0.7)
const MUTED = rgb(0.44, 0.51, 0.61)
const BORDER = rgb(0.82, 0.91, 0.93)
function wrapText(text: string, maxWidth: number, font: PDFFont, size: number, unicode: boolean): string[] {
  const safe = displayText(text, unicode)
  if (!safe) return []
  const words = safe.split(' ')
  const lines: string[] = []
  let line = ''
  for (const word of words) {
    const next = line ? `${line} ${word}` : word
    if (font.widthOfTextAtSize(next, size) > maxWidth) {
      if (line) lines.push(line)
      line = word
    } else {
      line = next
    }
  }
  if (line) lines.push(line)
  return lines
}

function imeiReportPdfFilename(imeiMasked: string): string {
  const tail = pdfSafeText(imeiMasked).replace(/[^0-9A-Za-z*xX]/g, '').slice(0, 24)
  return `redevicer-verify-${tail || 'report'}.pdf`
}

type PdfWriter = {
  page: PDFPage
  font: PDFFont
  fontBold: PDFFont
  unicode: boolean
  y: number
  margin: number
  pageWidth: number
  pageHeight: number
  contentWidth: number
}

function newPage(pdf: PDFDocument, w: PdfWriter): PDFPage {
  const page = pdf.addPage([595.28, 841.89])
  w.page = page
  w.y = w.pageHeight - 48
  page.drawRectangle({
    x: 0,
    y: 0,
    width: w.pageWidth,
    height: w.pageHeight,
    color: rgb(0.984, 0.992, 0.996),
  })
  return page
}

function ensureSpace(w: PdfWriter, needed: number, pdf: PDFDocument) {
  if (w.y - needed >= 56) return
  newPage(pdf, w)
}

type RoundedRectOpts = {
  x: number
  y: number
  width: number
  height: number
  color: RGB
  borderColor?: RGB
  borderWidth?: number
  borderRadius?: number
  opacity?: number
}

function drawRoundedRect(w: PdfWriter, opts: RoundedRectOpts) {
  const { x, y, width, height, color, borderColor, borderWidth, opacity } = opts
  const r = Math.min(opts.borderRadius ?? 0, width / 2, height / 2)
  if (r <= 0.5) {
    w.page.drawRectangle({
      x,
      y,
      width,
      height,
      color,
      borderColor,
      borderWidth,
      opacity,
    })
    return
  }
  const cy = y + height / 2
  w.page.drawRectangle({ x: x + r, y, width: width - 2 * r, height, color, opacity })
  w.page.drawCircle({ x: x + r, y: cy, size: r, color, opacity })
  w.page.drawCircle({ x: x + width - r, y: cy, size: r, color, opacity })
  if (borderColor && borderWidth) {
    w.page.drawRectangle({
      x: x + r,
      y,
      width: width - 2 * r,
      height,
      borderColor,
      borderWidth,
      color: undefined,
    })
  }
}

/** Capsule pill (pdf-lib rectangles are not rounded). */
function drawPill(
  w: PdfWriter,
  x: number,
  yBottom: number,
  width: number,
  height: number,
  fill: RGB,
  border?: RGB,
) {
  const r = height / 2
  const cy = yBottom + r
  w.page.drawRectangle({ x: x + r, y: yBottom, width: width - 2 * r, height, color: fill })
  w.page.drawCircle({ x: x + r, y: cy, size: r, color: fill })
  w.page.drawCircle({ x: x + width - r, y: cy, size: r, color: fill })
  if (border) {
    w.page.drawCircle({ x: x + r, y: cy, size: r, borderColor: border, borderWidth: 0.5, color: undefined })
    w.page.drawCircle({ x: x + width - r, y: cy, size: r, borderColor: border, borderWidth: 0.5, color: undefined })
  }
}

function drawRect(
  w: PdfWriter,
  x: number,
  yBottom: number,
  width: number,
  height: number,
  fill: RGB,
  border?: RGB,
) {
  drawRoundedRect(w, {
    x,
    y: yBottom,
    width,
    height,
    color: fill,
    borderColor: border,
    borderWidth: border ? 1 : 0,
  })
}

function chipColors(tone: ImeiReportPdfChipTone): { bg: RGB; fg: RGB; border: RGB } {
  switch (tone) {
    case 'pass':
      return { bg: rgb(0.9, 0.97, 0.95), fg: rgb(0.07, 0.53, 0.4), border: rgb(0.55, 0.84, 0.72) }
    case 'warning':
      return { bg: rgb(1, 0.96, 0.87), fg: rgb(0.65, 0.41, 0), border: rgb(0.9, 0.78, 0.45) }
    case 'fail':
      return { bg: rgb(1, 0.94, 0.95), fg: rgb(0.78, 0.25, 0.29), border: rgb(0.92, 0.58, 0.62) }
    case 'not_applicable':
      return { bg: rgb(0.94, 0.95, 0.96), fg: rgb(0.47, 0.53, 0.6), border: rgb(0.82, 0.87, 0.9) }
    default:
      return { bg: rgb(1, 0.94, 0.95), fg: rgb(0.78, 0.25, 0.29), border: rgb(0.92, 0.58, 0.62) }
  }
}

function sectionCardColors(tone: ImeiReportPdfChipTone): { fill: RGB; border: RGB } {
  switch (tone) {
    case 'pass':
      return { fill: rgb(0.94, 0.99, 0.97), border: rgb(0.55, 0.84, 0.72) }
    case 'warning':
      return { fill: rgb(1, 0.98, 0.94), border: rgb(0.9, 0.78, 0.45) }
    case 'fail':
    case 'unverified':
      return { fill: rgb(1, 0.97, 0.97), border: rgb(0.92, 0.58, 0.62) }
    case 'not_applicable':
      return { fill: rgb(0.97, 0.98, 0.98), border: rgb(0.82, 0.87, 0.9) }
    default:
      return { fill: rgb(1, 0.97, 0.97), border: rgb(0.92, 0.58, 0.62) }
  }
}

function statusColors(tone: ImeiReportPdfLabels['statusTone']): { bg: RGB; fg: RGB } {
  switch (tone) {
    case 'clean':
      return { bg: rgb(0.9, 0.98, 0.95), fg: rgb(0.07, 0.53, 0.4) }
    case 'attention':
      return { bg: rgb(1, 0.96, 0.87), fg: rgb(0.65, 0.41, 0) }
    case 'risk':
      return { bg: rgb(0.99, 0.92, 0.93), fg: rgb(0.77, 0.23, 0.27) }
    default:
      return { bg: rgb(0.93, 0.95, 0.96), fg: rgb(0.44, 0.51, 0.61) }
  }
}

function drawChipPill(
  w: PdfWriter,
  x: number,
  baselineY: number,
  text: string,
  tone: ImeiReportPdfLabels['statusTone'] | ImeiReportPdfChipTone,
  size = 8,
) {
  const safe = displayText(text, w.unicode)
  const font = w.fontBold
  const padX = 10
  const pillH = 20
  const pillW = font.widthOfTextAtSize(safe, size) + padX * 2
  const colors =
    tone === 'clean' ||
    tone === 'attention' ||
    tone === 'risk' ||
    tone === 'unknown' ||
    tone === 'invalid' ||
    tone === 'balance'
      ? { ...statusColors(tone as ImeiReportPdfLabels['statusTone']), border: BORDER }
      : chipColors(tone as ImeiReportPdfChipTone)
  const yBottom = baselineY - pillH / 2 - 2
  drawPill(w, x, yBottom, pillW, pillH, colors.bg, colors.border)
  w.page.drawText(safe, { x: x + padX, y: yBottom + 6, size, font, color: colors.fg })
  return { width: pillW, height: pillH }
}

const CHECK_ROW_GAP = 10

function measureCheckRowHeight(
  section: ImeiReportPdfSection,
  colW: number,
  w: PdfWriter,
  unicode: boolean,
): number {
  const chipLabel = displayText(section.chip, unicode)
  const chipW = w.fontBold.widthOfTextAtSize(chipLabel, 8) + 22
  const titleW = colW - 44 - chipW - 8
  const bodyW = colW - 44
  const titleLines = wrapText(section.title, Math.max(40, titleW), w.fontBold, 10, unicode)
  const hintLines = section.hint
    ? wrapText(displayText(section.hint, unicode), bodyW, w.fontBold, 7, unicode).slice(0, 2)
    : []
  let rowH = 46 + Math.max(0, titleLines.length - 1) * 12
  if (section.detail) rowH += 12
  if (hintLines.length) rowH += 4 + hintLines.length * 9
  return Math.max(56, rowH)
}

function drawCheckRowAt(
  w: PdfWriter,
  section: ImeiReportPdfSection,
  x: number,
  bottom: number,
  colW: number,
  rowH: number,
  unicode: boolean,
): void {
  const chipLabel = displayText(section.chip, unicode)
  const chipW = w.fontBold.widthOfTextAtSize(chipLabel, 8) + 22
  const titleW = colW - 44 - chipW - 8
  const bodyW = colW - 44
  const hintLines = section.hint
    ? wrapText(displayText(section.hint, unicode), bodyW, w.fontBold, 7, unicode).slice(0, 2)
    : []

  const card = sectionCardColors(section.chipTone)
  drawRoundedRect(w, {
    x,
    y: bottom,
    width: colW,
    height: rowH,
    color: card.fill,
    borderColor: card.border,
    borderWidth: 0.65,
    borderRadius: 12,
  })

  const iconCx = x + 22
  const iconCy = bottom + rowH - 28
  drawPdfSectionIcon(w, section.id, iconCx, iconCy, undefined)

  const titleLines = wrapText(section.title, Math.max(40, titleW), w.fontBold, 10, unicode)
  let titleY = bottom + rowH - 14
  for (const line of titleLines.slice(0, 2)) {
    w.page.drawText(line, { x: x + 40, y: titleY, size: 10, font: w.fontBold, color: NAVY })
    titleY -= 12
  }
  const chipBaseline = bottom + rowH - 14
  drawChipPill(w, x + colW - chipW - 8, chipBaseline + 2, chipLabel, section.chipTone, 8)

  let ty = bottom + rowH - 14 - titleLines.length * 12
  if (section.detail) {
    const detailLine = wrapText(displayText(section.detail, unicode), bodyW, w.font, 8, unicode)[0] ?? ''
    w.page.drawText(detailLine, { x: x + 40, y: ty, size: 8, font: w.font, color: MUTED })
    ty -= 10
  }
  for (const line of hintLines) {
    w.page.drawText(line, {
      x: x + 40,
      y: ty,
      size: 7,
      font: w.fontBold,
      color: rgb(0.77, 0.23, 0.27),
    })
    ty -= 9
  }
}

function drawCheckRowPair(
  w: PdfWriter,
  pdf: PDFDocument,
  left: ImeiReportPdfSection,
  right: ImeiReportPdfSection | undefined,
  unicode: boolean,
): void {
  const colW = (w.contentWidth - CHECK_ROW_GAP) / 2
  const rowH = Math.max(
    measureCheckRowHeight(left, colW, w, unicode),
    right ? measureCheckRowHeight(right, colW, w, unicode) : 0,
  )

  ensureSpace(w, rowH + 8, pdf)
  const bottom = w.y - rowH
  drawCheckRowAt(w, left, w.margin, bottom, colW, rowH, unicode)
  if (right) {
    drawCheckRowAt(w, right, w.margin + colW + CHECK_ROW_GAP, bottom, colW, rowH, unicode)
  }
  w.y = bottom - 8
}

function drawParagraph(
  w: PdfWriter,
  pdf: PDFDocument,
  text: string,
  size: number,
  x: number,
  maxWidth: number,
  color = MUTED,
  bold = false,
) {
  const font = bold ? w.fontBold : w.font
  const lines = wrapText(text, maxWidth, font, size, w.unicode)
  for (const line of lines) {
    ensureSpace(w, size + 8, pdf)
    w.page.drawText(line, { x, y: w.y, size, font, color })
    w.y -= size + 5
  }
}

async function resolvePdfFonts(
  pdf: PDFDocument,
  labels: ImeiReportPdfLabels,
): Promise<{ font: PDFFont; fontBold: PDFFont; unicode: boolean }> {
  try {
    const bundled = await loadImeiReportPdfFontBytes()
    const regularBytes = labels.fontRegularBytes ?? bundled.regular
    const boldBytes = labels.fontBoldBytes ?? bundled.bold
    pdf.registerFontkit(fontkit)
    const font = await pdf.embedFont(regularBytes)
    const fontBold = await pdf.embedFont(boldBytes)
    return { font, fontBold, unicode: true }
  } catch {
    const font = await pdf.embedFont(StandardFonts.Helvetica)
    const fontBold = await pdf.embedFont(StandardFonts.HelveticaBold)
    return { font, fontBold, unicode: false }
  }
}

export async function buildImeiReportPdfBytes(labels: ImeiReportPdfLabels): Promise<Uint8Array> {
  const pdf = await PDFDocument.create()
  const { font, fontBold, unicode } = await resolvePdfFonts(pdf, labels)

  pdf.setTitle(displayText(`${labels.reportLabel} · ${labels.imeiMasked}`, unicode))
  pdf.setAuthor('reDevicer')
  pdf.setCreator('reDevicer Verify')

  const pageWidth = 595.28
  const pageHeight = 841.89
  const margin = 40
  const contentWidth = pageWidth - margin * 2

  const w: PdfWriter = {
    page: pdf.addPage([pageWidth, pageHeight]),
    font,
    fontBold,
    unicode,
    y: pageHeight - 48,
    margin,
    pageWidth,
    pageHeight,
    contentWidth,
  }

  w.page.drawRectangle({
    x: 0,
    y: 0,
    width: pageWidth,
    height: pageHeight,
    color: rgb(0.984, 0.992, 0.996),
  })

  // —— Header ——
  w.page.drawCircle({ x: margin + 3, y: w.y - 2, size: 3, color: TEAL })
  w.page.drawText(displayText(labels.reportLabel, unicode), {
    x: margin + 12,
    y: w.y - 4,
    size: 8,
    font: fontBold,
    color: TEAL,
  })
  const serviceTitle = displayText(labels.serviceTitle, unicode)
  const serviceW = fontBold.widthOfTextAtSize(serviceTitle, 9)
  w.page.drawText(serviceTitle, {
    x: pageWidth - margin - serviceW,
    y: w.y - 2,
    size: 9,
    font: fontBold,
    color: NAVY,
  })
  w.y -= 28

  const hubHero = labels.heroHubArt === true
  const heroHeight = hubHero ? 172 : 128
  const heroBottom = w.y - heroHeight
  const heroTop = w.y

  drawRoundedRect(w, {
    x: margin,
    y: heroBottom,
    width: contentWidth,
    height: heroHeight,
    color: rgb(0.93, 0.98, 0.99),
    borderColor: rgb(0.78, 0.9, 0.92),
    borderWidth: 0.6,
    borderRadius: 20,
  })

  if (labels.phoneImageBytes?.length) {
    try {
      const img = await pdf.embedJpg(labels.phoneImageBytes).catch(() => pdf.embedPng(labels.phoneImageBytes!))
      if (hubHero) {
        const coverScale = Math.max(contentWidth / img.width, heroHeight / img.height)
        const imgW = img.width * coverScale
        const imgH = img.height * coverScale
        const focusX = 0.82
        let imgX = margin + contentWidth - imgW * focusX
        imgX = Math.min(margin, Math.max(imgX, margin + contentWidth - imgW))
        const imgY = heroBottom + (heroHeight - imgH) / 2
        w.page.drawImage(img, { x: imgX, y: imgY, width: imgW, height: imgH })
        drawRoundedRect(w, {
          x: margin,
          y: heroBottom,
          width: contentWidth * 0.46,
          height: heroHeight,
          color: rgb(1, 1, 1),
          opacity: 0.52,
          borderRadius: 20,
        })
      } else {
        const imgH = 96
        const scale = imgH / img.height
        const imgW = img.width * scale
        w.page.drawImage(img, {
          x: margin + contentWidth - imgW - 8,
          y: heroBottom + 6,
          width: imgW,
          height: imgH,
        })
      }
    } catch {
      /* optional asset */
    }
  }

  const heroTextX = margin + 18
  const heroTextMaxW = contentWidth * (hubHero ? 0.48 : 0.52)
  let hy = heroTop - (hubHero ? 18 : 22)
  const deviceTitle = displayText(labels.deviceLabel || labels.serviceTitle, unicode)
  for (const line of wrapText(deviceTitle, heroTextMaxW, fontBold, hubHero ? 16 : 17, unicode)) {
    w.page.drawText(line, { x: heroTextX, y: hy, size: hubHero ? 16 : 17, font: fontBold, color: NAVY })
    hy -= 18
  }
  hy -= 4
  w.page.drawText(displayText(labels.imeiMasked, unicode), {
    x: heroTextX,
    y: hy,
    size: 12,
    font: fontBold,
    color: rgb(0.23, 0.29, 0.38),
  })
  hy -= 15
  const meta = displayText(labels.metaLine || labels.checkedAt, unicode)
  if (meta) {
    for (const line of wrapText(meta, heroTextMaxW, font, 10, unicode)) {
      w.page.drawText(line, { x: heroTextX, y: hy, size: 10, font, color: MUTED })
      hy -= 12
    }
  }

  if (!hubHero) {
    const statusLabel = displayText(labels.statusLabel, unicode)
    const verdictColors = verdictMarkColors(labels.statusTone)
    const pillPadX = 12
    const pillH = 22
    const pillW = fontBold.widthOfTextAtSize(statusLabel, 10) + pillPadX * 2
    const pillX = margin + contentWidth - pillW - 16
    const pillY = heroBottom + 18
    drawRoundedRect(w, {
      x: pillX,
      y: pillY,
      width: pillW,
      height: pillH,
      color: verdictColors.bg,
      borderColor: rgb(0.78, 0.9, 0.88),
      borderWidth: 0.5,
      borderRadius: pillH / 2,
    })
    w.page.drawText(statusLabel, {
      x: pillX + pillPadX,
      y: pillY + 6,
      size: 10,
      font: fontBold,
      color: verdictColors.fg,
    })
  }

  const heroOverlap = hubHero ? 42 : 28
  w.y = heroBottom - heroOverlap

  // —— Verdict card (`.vr-verdict-card` + `.vr-result`) ——
  const verdictPadX = 18
  const verdictPadTop = 16
  const leftColW = 212
  const verdictInsetX = margin + 8
  const verdictInnerW = contentWidth - 16
  const rightX = verdictInsetX + Math.floor(verdictInnerW * 0.44) + 12
  const rightW = verdictInsetX + verdictInnerW - rightX - verdictPadX
  const summaryLines = wrapText(labels.summary, rightW, font, 10, unicode)

  const contentTop = w.y - verdictPadTop
  const markCy = contentTop - 26
  const headlineMaxW = Math.floor(verdictInnerW * 0.44) - 52 - 12

  let leftY = contentTop - 14
  for (const line of wrapText(labels.resultHeadline, headlineMaxW, fontBold, 12, unicode)) {
    leftY -= 14
  }
  leftY -= 2
  for (const line of wrapText(labels.coverageLine, headlineMaxW, font, 9, unicode)) {
    leftY -= 12
  }

  let ry = contentTop - 14
  for (const _line of summaryLines) {
    ry -= 13
  }
  if (labels.trustPills.length) {
    ry -= 8
    ry -= labels.trustPills.length * 14
  }

  const verdictHeight = w.y - Math.min(leftY, ry) + 12
  ensureSpace(w, verdictHeight + 20, pdf)
  const verdictTop = w.y
  const verdictBottom = verdictTop - verdictHeight
  const verdictX = margin + 8
  const verdictW = contentWidth - 16

  drawRoundedRect(w, {
    x: verdictX,
    y: verdictBottom,
    width: verdictW,
    height: verdictHeight,
    color: rgb(0.99, 1, 1),
    borderColor: rgb(0.82, 0.9, 0.92),
    borderWidth: 0.65,
    borderRadius: 22,
  })

  drawPdfVerdictIcon(w, verdictX + verdictPadX + 26, markCy, labels.statusTone, unicode, labels.resultHeadlineKey)

  leftY = contentTop - 14
  for (const line of wrapText(labels.resultHeadline, headlineMaxW, fontBold, 12, unicode)) {
    w.page.drawText(line, { x: verdictX + verdictPadX + 52 + 12, y: leftY, size: 12, font: fontBold, color: NAVY })
    leftY -= 14
  }
  leftY -= 2
  for (const line of wrapText(labels.coverageLine, headlineMaxW, font, 9, unicode)) {
    w.page.drawText(line, { x: verdictX + verdictPadX + 52 + 12, y: leftY, size: 9, font: fontBold, color: MUTED })
    leftY -= 12
  }

  ry = contentTop - 14
  for (const line of summaryLines) {
    w.page.drawText(line, { x: rightX, y: ry, size: 10, font, color: rgb(0.29, 0.36, 0.45) })
    ry -= 13
  }
  if (labels.trustPills.length) {
    ry -= 8
    for (const pill of labels.trustPills) {
      drawPdfTrustCheck(w, rightX, ry)
      w.page.drawText(displayText(pill, unicode), {
        x: rightX + 14,
        y: ry,
        size: 9,
        font: fontBold,
        color: rgb(0.29, 0.38, 0.47),
      })
      ry -= 14
    }
  }

  w.y = verdictBottom - 18

  // —— Checks ——
  ensureSpace(w, 40, pdf)
  w.page.drawText(displayText(labels.checksHeading, unicode), {
    x: margin,
    y: w.y,
    size: 12,
    font: fontBold,
    color: NAVY,
  })
  const covSum = displayText(labels.checksCoverageSummary, unicode)
  if (covSum) {
    w.page.drawText(covSum, {
      x: pageWidth - margin - font.widthOfTextAtSize(covSum, 9),
      y: w.y,
      size: 9,
      font: fontBold,
      color: MUTED,
    })
  }
  w.y -= 14
  drawParagraph(w, pdf, labels.checksLead, 9, margin, contentWidth, MUTED)
  w.y -= 6

  for (let i = 0; i < labels.sections.length; i += 2) {
    drawCheckRowPair(w, pdf, labels.sections[i], labels.sections[i + 1], unicode)
  }

  w.y -= 8
  ensureSpace(w, 70, pdf)
  drawParagraph(w, pdf, labels.secureLine, 9, margin, contentWidth, NAVY, true)
  drawParagraph(w, pdf, labels.privacyLine, 9, margin, contentWidth, MUTED)
  w.page.drawText(`redevicer.com · ${IMEI_REPORT_PDF_TEMPLATE_REV}`, {
    x: margin,
    y: 32,
    size: 7,
    font,
    color: rgb(0.55, 0.62, 0.72),
  })

  return pdf.save()
}

export function imeiReportPdfDownloadFilename(imeiMasked: string): string {
  return imeiReportPdfFilename(imeiMasked)
}

export { loadImeiReportPdfFontBytes } from './imeiReportPdfFonts'
